import React, { useEffect, useState, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import { Modal } from 'antd';

import { ChinaGeoCoordMap, coordInRegion } from '../../Libs/echarts';
import * as VThunk from '../../slices/vehicles/thunk';
import Crane3DViewer from '../3D/CraneModel';
import './VehicleDistributionMap.css';  // 导入样式文件

const fontSize = 15;
const carSymbol = 'path://M928 544h-35.52l-79.04-224a96 96 0 0 0-90.56-64H576a64 64 0 0 0-64 64v51.52L227.52 91.52A32 32 0 0 0 182.4 137.28L512 461.44V544h-48.96L184.64 203.84a32 32 0 1 0-49.28 40.32L380.48 544H288a32 32 0 0 0 0 64h224v192h-132.48a128 128 0 0 0-247.04 0H96a32 32 0 0 1-32-32v-128a32 32 0 0 1 32-32h64a32 32 0 0 0 0-64H96a96 96 0 0 0-96 96v128a96 96 0 0 0 96 96h36.48a128 128 0 0 0 247.04 0h264.96a128 128 0 0 0 247.04 0H928a96 96 0 0 0 96-96v-128a96 96 0 0 0-96-96z';

const selector = createSelector(
    state => state.Vehicles,
    state => ({
        dataList: state.dataList || [],
        loading: state.loading,
    }),
);

const BaseGeoChartOption = {
    backgroundColor: 'transparent',
    tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#1cccff',
        borderWidth: 1,
        padding: [10, 15],
        textStyle: {
            color: '#fff',
            fontSize: fontSize,
        },
        formatter: (params) => {
            if (params.data && params.data.vehicleCount > 0) {
                return `<div style="font-weight: bold; margin-bottom: 5px;">🚛 ${params.name}</div>
                        <div>车辆数量: ${params.data.vehicleCount} 辆</div>
                        <div>在线: ${params.data.onlineCount} 辆</div>`;
            }
            return '';
        }
    },
    geo: {
        map: 'china',
        show: true,
        aspectScale: 0.875,
        zoom: 1.5,
        layoutCenter: ['50%', '50%'],
        layoutSize: '100%',
        itemStyle: {
            areaColor: '#0c274b',
            borderColor: '#1cccff',
            borderWidth: 1.5
        },
        emphasis: {
            itemStyle: {
                areaColor: '#02102b'
            },
            label: {
                show: true,
                color: '#fff',
                fontSize: fontSize * 0.875,
                offset: [0, fontSize]
            }
        }
    },
    series: [
        {
            type: 'scatter',
            coordinateSystem: 'geo',
            zlevel: 1,
            symbol: carSymbol,
            symbolSize: 25,
            opacity: 0.9,
            itemStyle: {
                color: '#00ff88',
                shadowBlur: 10,
                shadowColor: '#00ff88',
            },
            data: []
        }
    ]
};

const VehicleDistributionMap = ({ style }) => {
    const { dataList, loading } = useSelector(selector);
    const [geoChartOption, setGeoChartOption] = useState({});
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [provinceVehicles, setProvinceVehicles] = useState([]);
    const [provinceModalVisible, setProvinceModalVisible] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [vehicleStatus, setVehicleStatus] = useState(null);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!dataList || dataList.length === 0) {
            dispatch(VThunk.getAll());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const processVehicleData = useCallback((vehicles) => {
        const provinceMap = {};
        
        vehicles.forEach((vehicle) => {
            const rmc = ((vehicle || {}).lastHeartbeat || {}).rmc;
            
            if (!rmc || !rmc.latitude || !rmc.longitude || rmc.status !== 'valid') {
                return;
            }

            const lng = parseFloat(rmc.longitude);
            const lat = parseFloat(rmc.latitude);

            if (isNaN(lng) || isNaN(lat)) {
                return;
            }

            const region = coordInRegion(lng, lat);
            const provinceName = region ? region.name : '未知区域';
            
            if (!provinceMap[provinceName]) {
                provinceMap[provinceName] = {
                    vehicles: [],
                    center: [lng, lat]
                };
            }
            
            provinceMap[provinceName].vehicles.push(vehicle);
        });

        const points = [];
        Object.keys(provinceMap).forEach((provinceName) => {
            const data = provinceMap[provinceName];
            const onlineCount = data.vehicles.filter(v => v.isAlive).length;
            
            points.push({
                name: provinceName,
                value: data.center,
                vehicleCount: data.vehicles.length,
                onlineCount: onlineCount,
                vehicles: data.vehicles,
                itemStyle: {
                    color: onlineCount > 0 ? '#00ff88' : '#ff2222',
                    shadowColor: onlineCount > 0 ? '#00ff88' : '#ff4444',
                }
            });
        });

        return { points, provinceMap };
    }, []);

    useEffect(() => {
        const options = _.cloneDeep(BaseGeoChartOption);
        const result = processVehicleData(dataList);
        options.series[0].data = result.points;
        setGeoChartOption(options);
    }, [dataList, processVehicleData]);

    const handleChartClick = useCallback((params) => {
        if (params.data && params.data.vehicles && params.data.vehicles.length > 0) {
            setSelectedProvince(params.name);
            setProvinceVehicles(params.data.vehicles);
            setProvinceModalVisible(true);
        }
    }, []);

    const fetchVehicleStatus = useCallback(async (vehicleId) => {
        try {
            const result = await dispatch(VThunk.getLastestStatusByVehicleId(vehicleId));
            const data = result.payload;
            if (data) {
                setVehicleStatus(data);
                setModalVisible(true);
            } else {
                setVehicleStatus(null);
                setModalVisible(true);
            }
        } catch (error) {
            console.error('获取车辆状态失败:', error);
        }
    }, [dispatch]);

    const handleSelectVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        setProvinceModalVisible(false);
        fetchVehicleStatus(vehicle.id);
    };

    const handleCloseProvinceModal = () => {
        setProvinceModalVisible(false);
        setSelectedProvince(null);
        setProvinceVehicles([]);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setSelectedVehicle(null);
        setVehicleStatus(null);
    };

    const onlineCount = provinceVehicles.filter(v => v.isAlive).length;
    const offlineCount = provinceVehicles.length - onlineCount;

    return (
        <>
            <ReactECharts 
                style={style} 
                option={geoChartOption} 
                notMerge={true} 
                lazyUpdate={true} 
                theme={'dark'} 
                onEvents={{ click: handleChartClick }}
            />

            {/* 省份车辆列表弹窗 */}
            <Modal
                title={`🚛 ${selectedProvince || '车辆列表'}`}
                open={provinceModalVisible}
                onCancel={handleCloseProvinceModal}
                footer={null}
                width={500}
                className="vehicle-list-modal"
            >
                <div className="vehicle-list-content" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {provinceVehicles.length > 0 ? (
                        <div style={{ padding: 16 }}>
                            {/* 统计信息栏 */}
                            <div className="vehicle-stats-bar">
                                <div className="stats-text">
                                    共 <span className="stats-count">{provinceVehicles.length}</span> 辆车
                                </div>
                                <div style={{ display: 'flex', gap: 16 }}>
                                    <div className="stats-online">
                                        <div className="status-dot online" />
                                        <span className="stats-online-text">在线 {onlineCount}</span>
                                    </div>
                                    <div className="stats-offline">
                                        <div className="status-dot offline" />
                                        <span className="stats-offline-text">离线 {offlineCount}</span>
                                    </div>
                                </div>
                            </div>

                            {/* 车辆列表 */}
                            {provinceVehicles.map((vehicle) => (
                                <div 
                                    key={vehicle.id}
                                    onClick={() => handleSelectVehicle(vehicle)}
                                    className={`vehicle-card ${vehicle.isAlive ? 'online' : 'offline'}`}
                                >
                                    <div className="vehicle-info">
                                        <div className="license-plate">{vehicle.licensePlate}</div>
                                        <div className="vehicle-detail">
                                            {vehicle.brand || '-'} - {vehicle.type || '未知类型'}
                                        </div>
                                    </div>
                                    <div className={`status-tag ${vehicle.isAlive ? 'online' : 'offline'}`}>
                                        {vehicle.isAlive ? '在线' : '离线'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-icon">🚛</div>
                            <div className="empty-title">暂无车辆数据</div>
                            <div className="empty-desc">该区域暂无车辆信息</div>
                        </div>
                    )}
                </div>
            </Modal>

            {/* 3D模型弹窗 */}
            <Modal
                title={`🚛 ${selectedVehicle?.licensePlate || '车辆详情'}`}
                open={modalVisible}
                onCancel={handleCloseModal}
                footer={null}
                width={1500}
                height={1000}
                bodyStyle={{ padding: 10 }}
            >
                <div style={{ height: '550px' }}>
                    {vehicleStatus ? (
                        <Crane3DViewer vehicleId={selectedVehicle.id} vehicleStatus={vehicleStatus} />
                    ) : (
                        <div style={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: '#6b7280',
                            background: '#f9fafb'
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>未查询到数据</div>
                            <div style={{ fontSize: '14px' }}>该车辆暂无实时状态数据，请稍后再试</div>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default VehicleDistributionMap;